import { useCallback, useEffect } from 'react';
import { dbui_v1_Query_GenerateStatus } from '@antimatterhq/asl-api-ts-v1';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import {
  queryTabAtomFamily,
  updateTabAtomFamily,
} from '@/atoms/query/queryAtoms';
import { useGetQueryStatus } from '@/hooks/dbui/useGetQueryStatus';
import { QueryTabStatus } from '@/types/query';

export type QueryStatusProps = {
  tabId: string;
};

export const getQueryTabStatus = (
  queryStatus: dbui_v1_Query_GenerateStatus | undefined,
): QueryTabStatus | undefined => {
  if (!queryStatus) {
    return undefined;
  }

  if (queryStatus.status === 'succeeded') {
    return 'success';
  }

  if (queryStatus.status === 'pending') {
    return 'pending';
  }

  if (queryStatus.status === 'running') {
    return 'running';
  }

  if (queryStatus.status === 'failed') {
    return 'error';
  }

  return undefined;
};

export const useQueryStatus = ({ tabId }: QueryStatusProps) => {
  const tab = useAtomValue(queryTabAtomFamily(tabId));

  // Get the query status if there is an active query
  const { data: queryStatus, isPending } = useGetQueryStatus({
    id: tab?.queryId,
    disabled: tab?.status === 'success',
  });

  const updateTabStatus = useAtomCallback(
    useCallback(
      (_get, set, status: dbui_v1_Query_GenerateStatus) => {
        set(updateTabAtomFamily(tabId), {
          status: getQueryTabStatus(status),
        });
      },
      [tabId],
    ),
  );

  // Update the tab status when the query status changes
  useEffect(() => {
    if (queryStatus) {
      updateTabStatus(queryStatus);
    }
  }, [queryStatus, updateTabStatus]);

  return {
    isPending,
  };
};
